import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  LocationOn,
  Phone,
  Email,
  Restaurant,
  Download,
  WhatsApp,
  ArrowForward
} from '@mui/icons-material';
import api from '../../services/api';
import menuCardService from '../../services/menuCardService';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { restaurantService } from '../../services/restaurantService';
import { useNotification } from '../../contexts/NotificationContext';
import outletService from '../../services/outletService';

const footerColor = {
  bg: '#680000',
  text: '#FFFFF0',
  white: '#FFFFF0',
  hover: '#351113E5'
}

// Standard font sizes for consistency
const textSizes = {
  xs: '0.75rem',    // 12px
  sm: '0.875rem',   // 14px
  md: '1rem',       // 16px
  lg: '1.125rem',   // 18px
  xl: '1.25rem',    // 20px
  xxl: '1.5rem',    // 24px
}

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSuccess, showError } = useNotification();
  const {
    selectedOutlet,
    menuItems,
    categories,
    loading: restaurantLoading
  } = useRestaurant();

  const [restaurantData, setRestaurantData] = useState(null);
  const [outletData, setOutletData] = useState([])
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    instagram: '',
    whatsapp: '',
    tiktok: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchRestaurantData = async () => {
      try {
        setLoading(true);
        const [restaurantResponse, socialResponse, outletResponse] = await Promise.all([
          api.get('/public/restaurant'),
          api.get('/public/social-links').catch(() => ({ data: null })),
          restaurantService.getPublicOutlets()
        ]);
        setRestaurantData(restaurantResponse.data);
        setOutletData(outletResponse);
        if (socialResponse.data) {
          setSocialLinks(socialResponse.data);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching restaurant data:', err);
        setError('Failed to load restaurant information');
        setRestaurantData({
          name: 'Restaurant Name',
          description: 'Authentic Indian cuisine served with love and tradition. Experience the rich flavors of India in every bite.',
          phone: '+1 (555) 123-4567',
          email: 'info@restaurant.com',
          website: null
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurantData();
  }, []);

  const handleDownloadMenuPDF = async () => {
    try {
      setDownloadingPDF(true);
      if (!selectedOutlet) {
        showError('Please select an outlet first');
        return;
      }
      if (!menuItems || menuItems.length === 0) {
        showError('No menu items available for download');
        return;
      }

      const pdfBlob = await menuCardService.generateMenuCardPDF(
        selectedOutlet,
        menuItems,
        categories
      );

      const filename = `Menu_${selectedOutlet.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      menuCardService.downloadPDF(pdfBlob, filename);

      showSuccess('Menu PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading menu PDF:', error);
      showError(`Failed to download menu PDF: ${error.message}`);
    } finally {
      setDownloadingPDF(false);
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        width: '100%',
        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : footerColor['bg'],
        color: theme.palette.mode === 'dark' ? 'grey.100' : footerColor['text'],
        mt: 'auto',
        py: { xs: 3, md: 4 },
        boxShadow: '0 -4px 8px rgba(0,0,0,0.1)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Main Footer Content */}
        <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="space-between" sx={{ maxWidth: '1440px', margin: {md:'0 auto '},}}>

          {/* Restaurant Info & Description */}
          <Grid item xs={12} md={3} lg={2.5}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', md: 'flex-start' },
              textAlign: { xs: 'center', md: 'left' }
            }}>
              {/* Logo */}
              <Box
                component="img"
                src={restaurantData?.logo_url}
                sx={{
                  width: { xs: '80px', sm: '100px', md: '120px' },
                  height: { xs: '80px', sm: '100px', md: '120px' },
                  borderRadius: '50%',
                  mb: 2,
                  objectFit: 'cover'
                }}
              />

              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading...</Typography>
                </Box>
              ) : error ? (
                <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
                  {error}
                </Alert>
              ) : (
                <Box sx={{
                  borderTop: '2px solid transparent',
                  borderBottom: '2px solid transparent',
                  borderImage: 'linear-gradient(90deg, #891313, #ECD73C, #891313) 1',
                  width: '100%'
                }}>
                  <Typography
                    variant="body2"
                    sx={{
                      py: 2,
                      fontSize: { xs: textSizes.xs, md: textSizes.sm },
                      lineHeight: 1.6,
                      fontStyle: 'italic'
                    }}
                  >
                    {restaurantData?.description || 'Authentic Indian cuisine served with love and tradition. Experience the rich flavors of India in every bite.'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2.5} lg={2}>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: textSizes.lg, md: textSizes.xl },
                fontWeight: 700,
                pb: 1,
                color: "#ECD73C",
                textAlign: { xs: 'center', md: 'left' },
                mb: 2
              }}
            >
              Quick Links
            </Typography>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              alignItems: { xs: 'center', md: 'flex-start' }
            }}>
              {['Home', 'Order Online', 'Table Reservation', 'Catering'].map((link, index) => (
                <Link
                  key={index}
                  href={link === 'Home' ? '/' : `/${link.toLowerCase().replace(' ', '-')}`}
                  color="inherit"
                  underline="hover"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: { xs: textSizes.xs, md: textSizes.sm },
                    '&:hover': { color: '#ECD73C' }
                  }}
                >
                  <ArrowForward sx={{ mr: 1, fontSize: { xs: '14px', md: '16px' } }} />
                  {link}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Contact & Outlets Info */}
          <Grid item xs={12} md={6} lg={7}>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: textSizes.lg, md: textSizes.xl },
                fontWeight: 700,
                pb: 1,
                color: "#ECD73C",
                textAlign: { xs: 'center', md: 'left' },
                mb: 2
              }}
            >
              Contact Us
            </Typography>

            <Grid container spacing={3}>
              {/* Outlets Section */}
              <Grid item xs={12} lg={8}>
                <Grid container spacing={2}>
                  {outletData && outletData.map((outlet, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ p: { xs: 1, sm: 2 } }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontSize: { xs: textSizes.sm, md: textSizes.md },
                            fontWeight: 700,
                            mb: 1
                          }}
                        >
                          {outlet?.name}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: textSizes.xs, md: textSizes.sm },
                            mb: 1.5,
                            lineHeight: 1.4
                          }}
                        >
                          {outlet?.address}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Phone sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: textSizes.xs, md: textSizes.sm }
                            }}
                          >
                            {outlet?.phone}
                          </Typography>
                        </Box>

                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontSize: { xs: textSizes.xs, md: textSizes.sm },
                            fontWeight: 700,
                            mb: 1
                          }}
                        >
                          Timing
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: textSizes.xs, md: textSizes.sm },
                            mb: 0.5
                          }}
                        >
                          Lunch : 11:00AM to 03:00PM
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: textSizes.xs, md: textSizes.sm }
                          }}
                        >
                          Dinner : 05:00PM to 11:00PM
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* Contact Help & Social Media */}
              <Grid item xs={12} lg={4}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  gap: 3
                }}>
                  {/* Need Help Section */}
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: textSizes.md, md: textSizes.lg },
                        fontWeight: 700,
                        pb: 1,
                        color: "#ECD73C"
                      }}
                    >
                      Need Help ?
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                      {restaurantData?.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: textSizes.xs, md: textSizes.sm } }}
                          >
                            {restaurantData.phone}
                          </Typography>
                        </Box>
                      )}
                      {restaurantData?.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: textSizes.xs, md: textSizes.sm } }}
                          >
                            {restaurantData.email}
                          </Typography>
                        </Box>
                      )}
                      {restaurantData?.website && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: textSizes.xs, md: textSizes.sm } }}
                          >
                            <Link
                              href={restaurantData.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="inherit"
                              underline="hover"
                            >
                              Visit Website
                            </Link>
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Follow Us Section */}
                  <Box sx={{ mt: 'auto' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: textSizes.md, md: textSizes.lg },
                        fontWeight: 700,
                        pb: 1,
                        color: "#ECD73C"
                      }}
                    >
                      Follow Us
                    </Typography>

                    <Box sx={{
                      display: 'flex',
                      gap: 1,
                      mb: 2,
                      flexWrap: 'wrap',
                      justifyContent: { xs: 'center', lg: 'flex-start' }
                    }}>
                      {socialLinks.facebook && (
                        <IconButton
                          href={socialLinks.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            bgcolor: footerColor['text'],
                            color: footerColor['bg'],
                            '&:hover': {
                              bgcolor: footerColor['white'],
                              transform: 'scale(1.1)'
                            },
                            fontSize: { xs: 'small', md: 'medium' }
                          }}
                        >
                          <Facebook />
                        </IconButton>
                      )}
                      {socialLinks.instagram && (
                        <IconButton
                          href={socialLinks.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            bgcolor: footerColor['text'],
                            color: footerColor['bg'],
                            '&:hover': {
                              bgcolor: footerColor['white'],
                              transform: 'scale(1.1)'
                            },
                            fontSize: { xs: 'small', md: 'medium' }
                          }}
                        >
                          <Instagram />
                        </IconButton>
                      )}
                      {socialLinks.whatsapp && (
                        <IconButton
                          href={socialLinks.whatsapp}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            bgcolor: footerColor['text'],
                            color: footerColor['bg'],
                            '&:hover': {
                              bgcolor: footerColor['white'],
                              transform: 'scale(1.1)'
                            },
                            fontSize: { xs: 'small', md: 'medium' }
                          }}
                        >
                          <WhatsApp />
                        </IconButton>
                      )}
                      {socialLinks.tiktok && (
                        <IconButton
                          href={socialLinks.tiktok}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            bgcolor: footerColor['text'],
                            color: footerColor['bg'],
                            '&:hover': {
                              bgcolor: footerColor['white'],
                              transform: 'scale(1.1)'
                            },
                            fontSize: { xs: 'small', md: 'medium' }
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold">TT</Typography>
                        </IconButton>
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: textSizes.xs, md: textSizes.sm },
                        textAlign: { xs: 'center', lg: 'left' }
                      }}
                    >
                      Stay updated with our latest offers and news
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider
          sx={{
            maxWidth:'1440px',
            margin:{md:'0 auto'},
            my: 5,
            height: "2px",
            border: "none",
            background: "linear-gradient(90deg, #891313, #ECD73C, #891313)"
          }}
        />

        {/* Copyright & Links */}
        <Box
          sx={{
            maxWidth:'1440px',
            margin:{md:'0 auto'},
            display: 'flex',
            pt:2,
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            textAlign: 'center'
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: textSizes.xs, md: textSizes.sm },
              order: { xs: 2, sm: 1 }
            }}
          >
            © {currentYear} {restaurantData?.name || 'Restaurant Name'}. All rights reserved.
          </Typography>
          <Box sx={{
            display: 'flex',
            gap: { xs: 2, md: 3 },
            flexWrap: 'wrap',
            justifyContent: 'center',
            order: { xs: 1, sm: 2 }
          }}>
            {['About Us', 'Contact Us', 'Privacy Policy', 'Terms of Service'].map((link, index) => (
              <Link
                key={index}
                href={`/${link.toLowerCase().replace(' ', '-')}`}
                color="inherit"
                underline="hover"
                variant="body2"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: { xs: textSizes.xs, md: textSizes.sm },
                  '&:hover': { color: '#ECD73C' }
                }}
              >
                {link}
              </Link>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;